/**
 * Shared logic for the desk's list screens — "Coming out", "Activities out" and
 * the parcel shelf. It sits beside visitor-exit-page.styled.tsx because those
 * three screens already share that stylesheet, and a helper that decides what a
 * row LOOKS like belongs with the rules for how a row looks.
 *
 * It is a module rather than a function exported from one of the pages: a page
 * importing another page pulls that page's whole component graph in with it,
 * and the first one to grow a route dependency makes the other two unbuildable.
 */

/**
 * THE INITIALS ON A ROW'S MARK.
 *
 * Built from the SAME string the row renders, never from a raw name field, so
 * the letters cannot disagree with the words underneath them. That matters more
 * here than it would elsewhere: visitor names are AES-GCM with a random IV, so
 * there is no canonical server-side form to fall back on — what the screen
 * decided to call somebody IS the name.
 *
 * Punctuation is stripped before the split, so "Stanley Parker — Unit 10" does
 * not yield "S—". Anything with no letters or digits left gets a dot rather than
 * a stray mark: an empty circle reads as a rendering fault, and a bullet reads
 * as "no name recorded", which is the truth.
 *
 * \p{L}/\p{N} with the u flag, not A-Za-z: a resident called Ксения has
 * initials too, and an ASCII class would have given every non-Latin name the
 * same dot.
 */
export const initialsOf = (label: string): string => {
  const words = String(label || '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return '·';

  // ONE WORD GIVES TWO LETTERS. A single initial collides constantly on lists
  // whose labels share a first letter - a desk with Cleaning, Contractor and
  // Collection open showed three identical discs, so the mark that exists to
  // tell rows apart was reporting them as the same. Multi-word labels keep
  // first-and-last, because a person's initials are the point there: John Smith
  // is JS, never JO.
  return (
    words.length > 1
      ? words[0][0] + words[words.length - 1][0]
      : words[0].slice(0, 2)
  ).toUpperCase();
};
